import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft } from 'lucide-react';
import DOMPurify from 'dompurify';
import { blogPosts } from '../blogData';

const BlogPostPage = () => {
  const { slug } = useParams();
  const post = blogPosts.find(p => p.slug === slug);

  if (!post) {
    return (
      <div className="section-padding" style={{ minHeight: '100vh', textAlign: 'center' }}>
        <div className="container">
          <h1 className="hero-medium">Post not found</h1>
          <Link to="/blog" className="btn-secondary" style={{ marginTop: '24px', display: 'inline-block' }}>
            <ArrowLeft size={16} style={{ marginRight: '8px' }} />
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  const faqSchema = post.faq && post.faq.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": post.faq.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
    }))
  } : null;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": post.meta?.title || post.title,
    "description": post.meta?.description || post.excerpt,
    "image": post.image,
    "datePublished": post.date,
    "author": { "@type": "Organization", "name": "Naru Tofu" },
    "publisher": {
      "@type": "Organization",
      "name": "Naru Tofu Organics Private Limited",
      "logo": {
        "@type": "ImageObject",
        "url": "https://customer-assets.emergentagent.com/job_transparent-tofu/artifacts/xqb7fx6c_naru%20tofu%20logo.jpg"
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>{post.meta?.title || post.title}</title>
        <meta name="description" content={post.meta?.description || post.excerpt} />
        <link rel="canonical" href={`https://narutofu.com${post.meta?.canonical || `/blog/${post.slug}`}`} />
        <meta property="og:title" content={post.meta?.title || post.title} />
        <meta property="og:description" content={post.meta?.description || post.excerpt} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://narutofu.com/blog/${post.slug}`} />
        {post.image && <meta property="og:image" content={post.image} />}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:title" content={post.meta?.title || post.title} />
        <meta property="twitter:description" content={post.meta?.description || post.excerpt} />
        {post.image && <meta property="twitter:image" content={post.image} />}
        <meta name="robots" content="index, follow" />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        {faqSchema && <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>}
      </Helmet>
      <article className="section-padding" style={{ minHeight: '100vh' }} data-testid="blog-post-page">
        <div className="container" style={{ maxWidth: '800px' }}>
          <Link to="/blog" className="btn-secondary" style={{ marginBottom: '32px', display: 'inline-flex', alignItems: 'center' }} data-testid="back-to-blog">
            <ArrowLeft size={16} style={{ marginRight: '8px' }} />
            Back to Blog
          </Link>
          
          <div style={{ marginBottom: '32px' }}>
            {post.tags && (
              <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
                {post.tags.map((tag, i) => (
                  <span key={tag} style={{ 
                    fontSize: '12px', 
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: 'var(--text-primary)',
                    background: 'var(--bg-secondary)',
                    padding: '4px 10px',
                    border: '1px solid var(--border-light)'
                  }}>
                    {tag}
                  </span>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
              <span className="body-small" style={{ color: 'var(--text-secondary)' }}>{post.date}</span>
              <span className="body-small" style={{ color: 'var(--text-secondary)' }}>by {post.author}</span>
            </div>
            
            <h1 className="hero-large" style={{ marginBottom: '24px', lineHeight: '1.2' }} data-testid="blog-post-title">{post.title}</h1>
          </div>

          {post.image && (
            <div style={{ marginBottom: '40px', overflow: 'hidden' }}>
              <img 
                src={post.image} 
                alt={post.title}
                style={{ width: '100%', height: 'auto', display: 'block' }}
                data-testid="blog-featured-image"
              />
            </div>
          )}
          
          <div 
            className="blog-content body-regular"
            style={{ lineHeight: '1.85' }}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content) }}
            data-testid="blog-post-content"
          />
          
          {/* CTA Section */}
          <div style={{ 
            marginTop: '48px', 
            padding: '32px', 
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-light)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px',
            textAlign: 'center'
          }} data-testid="blog-cta-section">
            <h3 className="heading-2">Ready to try fresh tofu?</h3>
            <p className="body-regular" style={{ color: 'var(--text-secondary)', maxWidth: '500px' }}>
              Order directly from Naru Tofu — fresh, preservative-free, delivered across Bangalore.
            </p>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', justifyContent: 'center' }}>
              <a
                href="https://wa.me/919663840931?text=Hi%2C%20I%27m%20interested%20in%20buying%20Naru%20Tofu%20products"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
                style={{ textDecoration: 'none' }}
                data-testid="blog-whatsapp-cta"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="#25D366" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px' }}>
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                </svg>
                WhatsApp to Order
              </a>
              <Link to="/products" className="btn-secondary" style={{ textDecoration: 'none' }} data-testid="blog-explore-products">
                Explore Products
              </Link>
            </div>
          </div>

          {/* FAQ Section */}
          {post.faq && post.faq.length > 0 && (
            <div style={{ marginTop: '64px' }} data-testid="blog-faq-section">
              <h2 className="heading-1" style={{ marginBottom: '32px' }}>Frequently Asked Questions</h2>
              {post.faq.map((item, index) => (
                <div key={item.question} style={{ 
                  marginBottom: '0', 
                  padding: '24px 0',
                  borderBottom: '1px solid var(--border-light)'
                }} data-testid={`faq-item-${index}`}>
                  <h3 className="heading-3" style={{ marginBottom: '10px' }}>{item.question}</h3>
                  <p className="body-regular" style={{ color: 'var(--text-secondary)', lineHeight: '1.7' }}>{item.answer}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </article>
    </>
  );
};

export default BlogPostPage;
